import { useEffect, useRef, useState, useCallback } from "react";
import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";
import { v4 as uuidv4 } from "uuid";

const LIVENESS_STEPS = [
  "ALIGN",
  "SMILE",
  "BLINK",
  "NOD",
  "TURN_LEFT",
  "TURN_RIGHT",
  "SUCCESS",
  "FAILED",
];

const CHALLENGES = ["SMILE", "BLINK", "NOD", "TURN_LEFT", "TURN_RIGHT"];

const MODEL_PATH =
  "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task";
const WASM_URL =
  "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.32/wasm";

const generateSessionId = () => {
  return uuidv4();
};

export const useFaceLiveness = (videoRef) => {
  const [state, setState] = useState({
    step: "ALIGN",
    progress: 0,
    instruction: "Align your face within the oval",
    isFaceCentered: false,
    isActive: false,
  });

  const faceLandmarkerRef = useRef(null);
  const requestRef = useRef(undefined);
  const lastProcessedTimeRef = useRef(0);

  // Tracking references for movement
  const initialNodRatioRef = useRef(null);
  const initialTurnRatioRef = useRef(null);
  const stepStartTimeRef = useRef(Date.now());
  const capturedImageRef = useRef(null);

  // Submission metadata tracking
  const sessionIdRef = useRef(null);
  const startTimestampRef = useRef(null);
  const completedChallengesRef = useRef([]);
  const challengesRef = useRef(CHALLENGES.slice());
  const submissionPayloadRef = useRef(null);
  const hasBuiltPayloadRef = useRef(false);
  const [submissionPayload, setSubmissionPayload] = useState(null);

  const resetSubmissionTracking = () => {
    sessionIdRef.current = null;
    startTimestampRef.current = null;
    completedChallengesRef.current = [];
    challengesRef.current = CHALLENGES.slice();
    submissionPayloadRef.current = null;
    hasBuiltPayloadRef.current = false;
    setSubmissionPayload(null);
  };

  const resetLiveness = () => {
    setState({
      step: "ALIGN",
      progress: 0,
      instruction: "Align your face within the oval",
      isFaceCentered: false,
      isActive: false,
    });
    capturedImageRef.current = null;
    initialNodRatioRef.current = null;
    initialTurnRatioRef.current = null;
    stepStartTimeRef.current = Date.now();
    resetSubmissionTracking();
  };

  const recordCompletedChallenge = (challenge) => {
    if (!CHALLENGES.includes(challenge)) return;
    if (!completedChallengesRef.current.includes(challenge)) {
      completedChallengesRef.current.push(challenge);
    }
  };

  useEffect(() => {
    const initLandmarker = async () => {
      const createFaceLandmarker = async (vision, delegate) =>
        FaceLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: MODEL_PATH,
            delegate,
          },
          outputFaceBlendshapes: true,
          runningMode: "VIDEO",
          numFaces: 1,
        });

      try {
        const vision = await FilesetResolver.forVisionTasks(WASM_URL);
        try {
          faceLandmarkerRef.current = await createFaceLandmarker(vision, "GPU");
        } catch (gpuError) {
          // Some browsers/devices fail GPU delegate initialization.
          console.warn(
            "FaceLandmarker GPU init failed. Falling back to CPU.",
            gpuError,
          );
          faceLandmarkerRef.current = await createFaceLandmarker(vision, "CPU");
        }
      } catch (error) {
        console.error("Failed to load MediaPipe:", error);
      }
    };

    initLandmarker();

    return () => {
      faceLandmarkerRef.current?.close();
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
    };
  }, []);

  const start = () => {
    // Reset any previous attempt data
    capturedImageRef.current = null;
    initialNodRatioRef.current = null;
    initialTurnRatioRef.current = null;
    stepStartTimeRef.current = Date.now();
    resetSubmissionTracking();

    sessionIdRef.current = generateSessionId();
    startTimestampRef.current = Date.now();
    challengesRef.current = CHALLENGES.slice();
    completedChallengesRef.current = [];

    setState((prev) => ({
      ...prev,
      isActive: true,
      step: "ALIGN",
      progress: 0,
    }));
  };

  const captureImage = () => {
    const video = videoRef.current?.video || videoRef.current;
    if (!video || !(video instanceof HTMLVideoElement)) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.drawImage(video, 0, 0);
      const base64 = canvas.toDataURL("image/jpeg");
      capturedImageRef.current = base64;
    }
  };

  const getNodRatio = (landmarks) => {
    const nose = landmarks[1];
    const forehead = landmarks[10];
    const chin = landmarks[152];
    return (nose.y - forehead.y) / (chin.y - forehead.y);
  };

  const getTurnRatio = (landmarks) => {
    const nose = landmarks[1];
    const leftEdge = landmarks[234];
    const rightEdge = landmarks[454];
    return (nose.x - leftEdge.x) / (rightEdge.x - leftEdge.x);
  };

  const checkAlignment = (landmarks) => {
    const nose = landmarks[1];
    const noseCentered =
      nose.x > 0.3 && nose.x < 0.7 && nose.y > 0.3 && nose.y < 0.7;
    const chin = landmarks[152];
    const forehead = landmarks[10];
    const isUpright = Math.abs(forehead.x - chin.x) < 0.14;
    return noseCentered && isUpright;
  };

  const moveToStep = (step, instruction) => {
    setState((prev) => ({
      ...prev,
      step,
      instruction,
      progress: getProgress(step),
    }));
    stepStartTimeRef.current = Date.now();
    initialNodRatioRef.current = null;
    initialTurnRatioRef.current = null;
  };

  const handleResults = (results) => {
    if (!results?.faceLandmarks?.length) return;

    const landmarks = results.faceLandmarks[0];
    const blendshapes = results.faceBlendshapes?.[0]?.categories || [];
    const isCentered = checkAlignment(landmarks);

    if (isCentered !== state.isFaceCentered) {
      setState((prev) => ({ ...prev, isFaceCentered: isCentered }));
    }

    if (state.step === "SUCCESS" || state.step === "FAILED") return;

    switch (state.step) {
      case "ALIGN":
        if (isCentered) {
          moveToStep("SMILE", "Please smile");
        }
        break;

      case "SMILE":
        if (
          getBlendshapeValue(blendshapes, "mouthSmileLeft") > 0.5 &&
          getBlendshapeValue(blendshapes, "mouthSmileRight") > 0.5
        ) {
          // Capture image as soon as a valid smile is detected
          captureImage();
          recordCompletedChallenge("SMILE");
          moveToStep("BLINK", "Please blink");
        }
        break;

      case "BLINK":
        if (
          getBlendshapeValue(blendshapes, "eyeBlinkLeft") > 0.6 &&
          getBlendshapeValue(blendshapes, "eyeBlinkRight") > 0.6
        ) {
          recordCompletedChallenge("BLINK");
          moveToStep("NOD", "Please nod your head");
        }
        break;

      case "NOD":
        const nodRatio = getNodRatio(landmarks);
        if (initialNodRatioRef.current === null) {
          initialNodRatioRef.current = nodRatio;
        } else if (Math.abs(nodRatio - initialNodRatioRef.current) > 0.05) {
          recordCompletedChallenge("NOD");
          moveToStep("TURN_LEFT", "Please turn your head left");
        }
        break;

      case "TURN_LEFT":
        const turnLeftRatio = getTurnRatio(landmarks);
        if (initialTurnRatioRef.current === null) {
          initialTurnRatioRef.current = turnLeftRatio;
        } else if (turnLeftRatio > 0.8) {
          // Require a slightly stronger left turn
          recordCompletedChallenge("TURN_LEFT");
          moveToStep("TURN_RIGHT", "Please turn your head right");
        }
        break;

      case "TURN_RIGHT":
        const turnRightRatio = getTurnRatio(landmarks);
        if (turnRightRatio < 0.2) {
          // Require a slightly stronger right turn
          recordCompletedChallenge("TURN_RIGHT");
          moveToStep("SUCCESS", "Verification successful");

          // Build the backend payload exactly once (guarded against frame repetition)
          if (!hasBuiltPayloadRef.current) {
            hasBuiltPayloadRef.current = true;

            const endTimestampMs = Date.now();
            const completedAtIso = new Date(endTimestampMs).toISOString();
            const sessionDurationMs =
              startTimestampRef.current !== null
                ? endTimestampMs - startTimestampRef.current
                : 0;

            const payload = {
              sessionId: sessionIdRef.current,
              isSuccessful: true,
              imageBase64: capturedImageRef.current,
              metadata: {
                timestamp: startTimestampRef.current,
                completedAt: completedAtIso,
                verificationResult: true,
                sessionDuration: sessionDurationMs,
                completedChallenges: completedChallengesRef.current,
                challenges: challengesRef.current,
                challengeCount: challengesRef.current.length,
                finalCapture: {
                  verificationResult: true,
                  sessionDuration: sessionDurationMs,
                },
              },
            };

            submissionPayloadRef.current = payload;
            setSubmissionPayload(payload);
          }
        }
        break;
    }
  };

  const processFrame = useCallback(() => {
    const video = videoRef.current?.video || videoRef.current;
    if (
      !state.isActive ||
      !faceLandmarkerRef.current ||
      !video ||
      video.readyState < 2
    ) {
      requestRef.current = requestAnimationFrame(processFrame);
      return;
    }

    const startTimeMs = performance.now();
    if (video.currentTime !== lastProcessedTimeRef.current) {
      lastProcessedTimeRef.current = video.currentTime;
      const results = faceLandmarkerRef.current.detectForVideo(
        video,
        startTimeMs,
      );
      handleResults(results);
    }

    requestRef.current = requestAnimationFrame(processFrame);
  }, [videoRef, state.isActive, state.step]);

  useEffect(() => {
    requestRef.current = requestAnimationFrame(processFrame);
    return () => {
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
    };
  }, [processFrame]);

  const getBlendshapeValue = (categories, name) => {
    const category = categories.find((c) => c.categoryName === name);
    return category ? category.score : 0;
  };

  const getProgress = (step) => {
    const steps = LIVENESS_STEPS.filter((s) => s !== "FAILED");
    return (steps.indexOf(step) / (steps.length - 1)) * 100;
  };

  return {
    state,
    start,
    resetLiveness,
    submissionPayload,
  };
};
